import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { DAILY_SUMMARY } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { DAILY_SUMMARY_LIST_PATH } from '../../navigations/path';

const CreateDailySummary = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [assignNumberOptions, setAssignNumberOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const [formData, setFormData] = useState({
    no_of_dials: '',
    answered_calls: '',
    call_duration_hours: 0,
    call_duration_minutes: 0,
    interested_leads: '',
    date: today,
    assigned_number_id: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchAssignNumbers = async () => {
      try {
        setLoadingOptions(true);
        const { data } = await api.get(DAILY_SUMMARY.ASSIGNED_NUMBERS);
        const raw = data?.data?.list ?? data?.list ?? extractList(data);
        const list = (Array.isArray(raw) ? raw : []).map((item) => ({
          id: item.id ?? item.uuid,
          name: item.name ?? item.number ?? item.label ?? String(item.id),
        }));
        setAssignNumberOptions(list);
      } catch (err) {
        console.warn('Assign numbers API not ready:', err?.response?.data?.message || err.message);
        setAssignNumberOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchAssignNumbers();
  }, []);

  const handleChange = (field) => (e) => {
    let value = e.target.value;
    if (field === 'call_duration_hours' || field === 'call_duration_minutes') {
      value = parseInt(value, 10);
      if (Number.isNaN(value)) value = field === 'call_duration_minutes' ? 0 : 0;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (formData.no_of_dials === '' && formData.no_of_dials !== 0) next.no_of_dials = 'Required';
    if (formData.answered_calls === '' && formData.answered_calls !== 0) next.answered_calls = 'Required';
    if (formData.interested_leads === '' && formData.interested_leads !== 0) next.interested_leads = 'Required';
    if (!formData.date?.trim()) next.date = 'Required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildCallDuration = () => {
    const h = Number(formData.call_duration_hours) || 0;
    const m = Number(formData.call_duration_minutes) || 0;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        no_of_dials: String(Number(formData.no_of_dials) || 0),
        answered_calls: String(Number(formData.answered_calls) || 0),
        call_duration: buildCallDuration(),
        interested_leads: String(Number(formData.interested_leads) || 0),
        report_date: formData.date,
      };
      if (formData.assigned_number_id) payload.dialed_from_assigned_number_id = formData.assigned_number_id;
      await api.post(DAILY_SUMMARY.CREATE, payload);
      toast.success('Daily summary added.');
      navigate(DAILY_SUMMARY_LIST_PATH);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add daily summary');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={600} color="text.primary">
            Add Daily Summary
          </Typography>
        </Box>

        <Card variant="outlined" sx={{ boxShadow: 1 }}>
          <CardContent sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="No. of Dials"
                    type="number"
                    value={formData.no_of_dials}
                    onChange={handleChange('no_of_dials')}
                    error={!!errors.no_of_dials}
                    helperText={errors.no_of_dials}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Answered Calls"
                    type="number"
                    value={formData.answered_calls}
                    onChange={handleChange('answered_calls')}
                    error={!!errors.answered_calls}
                    helperText={errors.answered_calls}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Hours</InputLabel>
                      <Select
                        value={formData.call_duration_hours}
                        label="Hours"
                        onChange={handleChange('call_duration_hours')}
                      >
                        {Array.from({ length: 25 }, (_, i) => (
                          <MenuItem key={i} value={i}>{i}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>Minutes</InputLabel>
                      <Select
                        value={formData.call_duration_minutes}
                        label="Minutes"
                        onChange={handleChange('call_duration_minutes')}
                      >
                        {Array.from({ length: 60 }, (_, i) => (
                          <MenuItem key={i} value={i}>{i}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Interested Leads (> 1 Mint)"
                    type="number"
                    value={formData.interested_leads}
                    onChange={handleChange('interested_leads')}
                    error={!!errors.interested_leads}
                    helperText={errors.interested_leads}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange('date')}
                    error={!!errors.date}
                    helperText={errors.date}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: today }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" disabled={loadingOptions}>
                    <InputLabel>Dial From / Assign Number</InputLabel>
                    <Select
                      value={formData.assigned_number_id}
                      onChange={handleChange('assigned_number_id')}
                      label="Dial From / Assign Number"
                    >
                      <MenuItem value="">Select number</MenuItem>
                      {assignNumberOptions.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sx={{ pt: 1 }}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={() => navigate(DAILY_SUMMARY_LIST_PATH)} disabled={submitting}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={submitting}>
                      {submitting ? <CircularProgress size={24} /> : 'Create'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateDailySummary;
